import {
  CodeLanguage,
  CodeSnippetParams,
  DownloadDirectParams,
  DownloadConfigParams,
  DownloadFileListParams,
  DownloadFilePartParams,
  DirectoryGetDirectParams,
  DirectoryGetConfigParams,
  DirectoryUploadParams,
  DirectoryDeleteParams,
} from '@core/@models/ApiManualModel';
import { axiosRequestConfig } from '@core/axios';

export const getBaseUrl = (): string => {
  const baseUrl = axiosRequestConfig.baseURL || '';
  return baseUrl.replace(/\/$/, '');
};

const encodeParam = (str: string): string => {
  return encodeURIComponent(str);
};

const normalizePathParam = (path: string): string => {
  if (!path || typeof path !== 'string') return path || '';
  return path.replace(/^\/+/, '');
};

export const generateDownloadDirectCurl = (
  params: DownloadDirectParams
): string => {
  const baseUrl = getBaseUrl();
  const noDirParam = params.noDirectory ? '&noDirectory=true' : '';
  return `curl -OJ "${baseUrl}/external/get/file?group=${encodeParam(
    params.group
  )}&fileList=${encodeParam(params.fileList)}&beginDate=${encodeParam(
    params.beginDate
  )}&endDate=${encodeParam(params.endDate)}${noDirParam}" \\
  -H "Authorization: Bearer YOUR_BEARER_TOKEN"`;
};

export const generateDownloadDirectPython = (
  params: DownloadDirectParams
): string => {
  const baseUrl = getBaseUrl();
  return `import requests

BASE_URL = "${baseUrl}"
TOKEN = "YOUR_BEARER_TOKEN"

response = requests.get(
    f"{BASE_URL}/external/get/file",
    params={
        "group": "${params.group}",
        "fileList": "${params.fileList}",
        "beginDate": "${params.beginDate}",
        "endDate": "${params.endDate}"${
    params.noDirectory ? ',\n        "noDirectory": True' : ''
  }
    },
    headers={"Authorization": f"Bearer {TOKEN}"},
    stream=True
)

if response.status_code == 200:
    filename = response.headers.get('content-disposition', '').split('filename=')[-1].strip('"')
    with open(filename or "downloaded_file", 'wb') as f:
        for chunk in response.iter_content(chunk_size=8192):
            f.write(chunk)
    print(f"Downloaded: {filename}")
else:
    print(f"Error: {response.status_code}")`;
};

export const generateDownloadDirectJava = (
  params: DownloadDirectParams
): string => {
  const baseUrl = getBaseUrl();
  const noDirParam = params.noDirectory ? '&noDirectory=true' : '';
  return `import java.net.URI;
import java.net.http.*;
import java.nio.file.*;

public class DownloadFile {
    public static void main(String[] args) throws Exception {
        String BASE_URL = "${baseUrl}";
        String TOKEN = "YOUR_BEARER_TOKEN";

        String url = String.format(
            "%s/external/get/file?group=%s&fileList=%s&beginDate=%s&endDate=%s${noDirParam}",
            BASE_URL, "${params.group}", "${params.fileList}", "${params.beginDate}", "${params.endDate}"
        );

        HttpClient client = HttpClient.newHttpClient();
        HttpRequest request = HttpRequest.newBuilder()
            .uri(URI.create(url))
            .header("Authorization", "Bearer " + TOKEN)
            .GET()
            .build();

        HttpResponse<Path> response = client.send(
            request, 
            HttpResponse.BodyHandlers.ofFile(Path.of("downloaded_file"))
        );

        System.out.println("Status: " + response.statusCode());
    }
}`;
};

export const generateDownloadDirectJavaScript = (
  params: DownloadDirectParams
): string => {
  const baseUrl = getBaseUrl();
  return `const BASE_URL = "${baseUrl}";
const TOKEN = "YOUR_BEARER_TOKEN";

const params = new URLSearchParams({
  group: "${params.group}",
  fileList: "${params.fileList}",
  beginDate: "${params.beginDate}",
  endDate: "${params.endDate}"${
    params.noDirectory ? ',\n  noDirectory: "true"' : ''
  }
});

fetch(\`\${BASE_URL}/external/get/file?\${params}\`, {
  method: 'GET',
  headers: { 'Authorization': \`Bearer \${TOKEN}\` }
})
.then(response => response.blob())
.then(blob => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'downloaded_file';
  a.click();
})
.catch(error => console.error('Error:', error));`;
};

export const generateDownloadConfigCurl = (
  params: DownloadConfigParams
): string => {
  const baseUrl = getBaseUrl();
  const noDirParam = params.noDirectory ? '&noDirectory=true' : '';
  return `curl -OJ "${baseUrl}/external/get/file?configName=${encodeParam(
    params.configName
  )}&group=${encodeParam(params.group)}&beginDate=${encodeParam(
    params.beginDate
  )}&endDate=${encodeParam(params.endDate)}${noDirParam}" \\
  -H "Authorization: Bearer YOUR_BEARER_TOKEN"`;
};

export const generateDownloadConfigPython = (
  params: DownloadConfigParams
): string => {
  return `import requests

# === Configuration ===
BASE_URL = ${getBaseUrl()}
TOKEN = "YOUR_BEARER_TOKEN"

# === Parameters ===
CONFIG_NAME = "${params.configName}"
GROUP = "${params.group}"
BEGIN_DATE = "${params.beginDate}"
END_DATE = "${params.endDate}"

# === Request ===
response = requests.get(
    f"{BASE_URL}/external/get/file",
    params={
        "configName": CONFIG_NAME,
        "group": GROUP,
        "beginDate": BEGIN_DATE,
        "endDate": END_DATE${
          params.noDirectory ? ',\n        "noDirectory": True' : ''
        }
    },
    headers={"Authorization": f"Bearer {TOKEN}"},
    stream=True
)

# === Save file ===
if response.status_code == 200:
    filename = response.headers.get('content-disposition', '').split('filename=')[-1].strip('"')
    with open(filename or "downloaded_file", 'wb') as f:
        for chunk in response.iter_content(chunk_size=8192):
            f.write(chunk)
    print(f"Downloaded: {filename}")
else:
    print(f"Error: {response.status_code}")`;
};

export const generateDownloadConfigJava = (
  params: DownloadConfigParams
): string => {
  return `import java.net.URI;
import java.net.http.*;
import java.nio.file.*;

public class DownloadFileByConfig {
    public static void main(String[] args) throws Exception {
        // === Configuration ===
        String BASE_URL = ${getBaseUrl()};
        String TOKEN = "YOUR_BEARER_TOKEN";

        // === Parameters ===
        String CONFIG_NAME = "${params.configName}";
        String GROUP = "${params.group}";
        String BEGIN_DATE = "${params.beginDate}";
        String END_DATE = "${params.endDate}";

        // === Build URL ===
        String url = String.format(
            "%s/external/get/file?configName=%s&group=%s&beginDate=%s&endDate=%s${
              params.noDirectory ? '&noDirectory=true' : ''
            }",
            BASE_URL, CONFIG_NAME, GROUP, BEGIN_DATE, END_DATE
        );

        // === Request ===
        HttpClient client = HttpClient.newHttpClient();
        HttpRequest request = HttpRequest.newBuilder()
            .uri(URI.create(url))
            .header("Authorization", "Bearer " + TOKEN)
            .GET()
            .build();

        HttpResponse<Path> response = client.send(
            request, 
            HttpResponse.BodyHandlers.ofFile(Path.of("downloaded_file"))
        );

        System.out.println("Status: " + response.statusCode());
    }
}`;
};

export const generateDownloadConfigJavaScript = (
  params: DownloadConfigParams
): string => {
  return `// === Configuration ===
const BASE_URL = ${getBaseUrl()};
const TOKEN = "YOUR_BEARER_TOKEN";

// === Parameters ===
const CONFIG_NAME = "${params.configName}";
const GROUP = "${params.group}";
const BEGIN_DATE = "${params.beginDate}";
const END_DATE = "${params.endDate}";

// === Request ===
const params = new URLSearchParams({
  configName: CONFIG_NAME,
  group: GROUP,
  beginDate: BEGIN_DATE,
  endDate: END_DATE${params.noDirectory ? ',\n  noDirectory: "true"' : ''}
});

fetch(\`\${BASE_URL}/external/get/file?\${params}\`, {
  method: 'GET',
  headers: {
    'Authorization': \`Bearer \${TOKEN}\`
  }
})
.then(response => response.blob())
.then(blob => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'downloaded_file';
  a.click();
})
.catch(error => console.error('Error:', error));`;
};

export const generateFileListCurl = (
  params: DownloadFileListParams
): string => {
  const baseUrl = getBaseUrl();
  return `curl -OJ "${baseUrl}/external/get/file/list?group=${encodeParam(
    params.group
  )}&fileList=${encodeParam(params.fileList)}&beginDate=${encodeParam(
    params.beginDate
  )}&endDate=${encodeParam(params.endDate)}" \\
  -H "Authorization: Bearer YOUR_BEARER_TOKEN" \\
  -H "Accept: application/json"`;
};

export const generateFileListPython = (
  params: DownloadFileListParams
): string => {
  return `import requests

# === Configuration ===
BASE_URL = ${getBaseUrl()}
TOKEN = "YOUR_BEARER_TOKEN"

# === Parameters ===
GROUP = "${params.group}"
FILE_LIST = "${params.fileList}"
BEGIN_DATE = "${params.beginDate}"
END_DATE = "${params.endDate}"

# === Request ===
response = requests.get(
    f"{BASE_URL}/external/get/file/list",
    params={
        "group": GROUP,
        "fileList": FILE_LIST,
        "beginDate": BEGIN_DATE,
        "endDate": END_DATE
    },
    headers={"Authorization": f"Bearer {TOKEN}"}
)

# === Process response ===
if response.status_code == 200:
    data = response.json()
    print("Available files:")
    for file in data.get("files", []):
        print(f"  - {file['name']} ({file.get('slotDate', 'N/A')})")
else:
    print(f"Error: {response.status_code}")`;
};

export const generateFileListJava = (
  params: DownloadFileListParams
): string => {
  return `import java.net.URI;
import java.net.http.*;

public class GetFileList {
    public static void main(String[] args) throws Exception {
        // === Configuration ===
        String BASE_URL = ${getBaseUrl()};
        String TOKEN = "YOUR_BEARER_TOKEN";

        // === Parameters ===
        String GROUP = "${params.group}";
        String FILE_LIST = "${params.fileList}";
        String BEGIN_DATE = "${params.beginDate}";
        String END_DATE = "${params.endDate}";

        // === Build URL ===
        String url = String.format(
            "%s/external/get/file/list?group=%s&fileList=%s&beginDate=%s&endDate=%s",
            BASE_URL, GROUP, FILE_LIST, BEGIN_DATE, END_DATE
        );

        // === Request ===
        HttpClient client = HttpClient.newHttpClient();
        HttpRequest request = HttpRequest.newBuilder()
            .uri(URI.create(url))
            .header("Authorization", "Bearer " + TOKEN)
            .header("Accept", "application/json")
            .GET()
            .build();

        HttpResponse<String> response = client.send(
            request, 
            HttpResponse.BodyHandlers.ofString()
        );

        System.out.println("Response: " + response.body());
    }
}`;
};

export const generateFileListJavaScript = (
  params: DownloadFileListParams
): string => {
  return `// === Configuration ===
const BASE_URL = ${getBaseUrl()};
const TOKEN = "YOUR_BEARER_TOKEN";

// === Parameters ===
const GROUP = "${params.group}";
const FILE_LIST = "${params.fileList}";
const BEGIN_DATE = "${params.beginDate}";
const END_DATE = "${params.endDate}";

// === Request ===
const params = new URLSearchParams({
  group: GROUP,
  fileList: FILE_LIST,
  beginDate: BEGIN_DATE,
  endDate: END_DATE
});

fetch(\`\${BASE_URL}/external/get/file/list?\${params}\`, {
  method: 'GET',
  headers: {
    'Authorization': \`Bearer \${TOKEN}\`,
    'Accept': 'application/json'
  }
})
.then(response => response.json())
.then(data => {
  console.log('Available files:');
  data.files.forEach(file => {
    console.log(\`  - \${file.name} (\${file.slotDate || 'N/A'})\`);
  });
})
.catch(error => console.error('Error:', error));`;
};

export const generateFilePartCurl = (
  params: DownloadFilePartParams
): string => {
  const baseUrl = getBaseUrl();
  return `curl -OJ "${baseUrl}/external/get/file/part?group=${encodeParam(
    params.group
  )}&fileList=${encodeParam(params.fileList)}&fileName=${encodeParam(
    params.fileName
  )}&date=${encodeParam(params.date)}" \\
  -H "Authorization: Bearer YOUR_BEARER_TOKEN"`;
};

export const generateFilePartPython = (
  params: DownloadFilePartParams
): string => {
  return `import requests

# === Configuration ===
BASE_URL = ${getBaseUrl()}
TOKEN = "YOUR_BEARER_TOKEN"

# === Parameters ===
GROUP = "${params.group}"
FILE_LIST = "${params.fileList}"
FILE_NAME = "${params.fileName}"
DATE = "${params.date}"

# === Request ===
response = requests.get(
    f"{BASE_URL}/external/get/file/part",
    params={
        "group": GROUP,
        "fileList": FILE_LIST,
        "fileName": FILE_NAME,
        "date": DATE
    },
    headers={"Authorization": f"Bearer {TOKEN}"},
    stream=True
)

# === Save file ===
if response.status_code == 200:
    with open(FILE_NAME, 'wb') as f:
        for chunk in response.iter_content(chunk_size=8192):
            f.write(chunk)
    print(f"Downloaded: {FILE_NAME}")
else:
    print(f"Error: {response.status_code}")`;
};

export const generateFilePartJava = (
  params: DownloadFilePartParams
): string => {
  return `import java.net.URI;
import java.net.http.*;
import java.nio.file.*;

public class DownloadFilePart {
    public static void main(String[] args) throws Exception {
        // === Configuration ===
        String BASE_URL = ${getBaseUrl()};
        String TOKEN = "YOUR_BEARER_TOKEN";

        // === Parameters ===
        String GROUP = "${params.group}";
        String FILE_LIST = "${params.fileList}";
        String FILE_NAME = "${params.fileName}";
        String DATE = "${params.date}";

        // === Build URL ===
        String url = String.format(
            "%s/external/get/file/part?group=%s&fileList=%s&fileName=%s&date=%s",
            BASE_URL, GROUP, FILE_LIST, FILE_NAME, DATE
        );

        // === Request ===
        HttpClient client = HttpClient.newHttpClient();
        HttpRequest request = HttpRequest.newBuilder()
            .uri(URI.create(url))
            .header("Authorization", "Bearer " + TOKEN)
            .GET()
            .build();

        HttpResponse<Path> response = client.send(
            request, 
            HttpResponse.BodyHandlers.ofFile(Path.of(FILE_NAME))
        );

        System.out.println("Downloaded: " + FILE_NAME);
    }
}`;
};

export const generateFilePartJavaScript = (
  params: DownloadFilePartParams
): string => {
  return `// === Configuration ===
const BASE_URL = ${getBaseUrl()};
const TOKEN = "YOUR_BEARER_TOKEN";

// === Parameters ===
const GROUP = "${params.group}";
const FILE_LIST = "${params.fileList}";
const FILE_NAME = "${params.fileName}";
const DATE = "${params.date}";

// === Request ===
const params = new URLSearchParams({
  group: GROUP,
  fileList: FILE_LIST,
  fileName: FILE_NAME,
  date: DATE
});

fetch(\`\${BASE_URL}/external/get/file/part?\${params}\`, {
  method: 'GET',
  headers: {
    'Authorization': \`Bearer \${TOKEN}\`
  }
})
.then(response => response.blob())
.then(blob => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = FILE_NAME;
  a.click();
  console.log(\`Downloaded: \${FILE_NAME}\`);
})
.catch(error => console.error('Error:', error));`;
};

export const generateDirectoryGetDirectCurl = (
  params: DirectoryGetDirectParams
): string => {
  const baseUrl = getBaseUrl();
  const isSingle = params.singleFile;

  const dirPath = normalizePathParam(params.directoryServicePath);
  if (isSingle) {
    return `curl -OJ "${baseUrl}/external/get/directory-service/file?directoryServicePath=${dirPath}" \\
  -H "Authorization: Bearer YOUR_BEARER_TOKEN"`;
  }

  const includeSubParam = params.includeSub ? '&includeSub=true' : '';
  return `curl -OJ "${baseUrl}/external/get/directory-service/file?directoryServicePath=${dirPath}&fileList=${encodeParam(
    params.fileList
  )}${includeSubParam}" \\
  -H "Authorization: Bearer YOUR_BEARER_TOKEN"`;
};

export const generateDirectoryGetDirectPython = (
  params: DirectoryGetDirectParams
): string => {
  const isSingle = params.singleFile;
  const outputFile = isSingle
    ? params.fileList || 'downloaded_file'
    : params.isPattern
    ? 'downloaded_files.zip'
    : 'downloaded_file';

  if (isSingle) {
    return `import requests

# === Configuration ===
BASE_URL = ${getBaseUrl()}
TOKEN = "YOUR_BEARER_TOKEN"

# === Parameters (single file - no ZIP wrapper) ===
DIRECTORY_PATH = "${normalizePathParam(params.directoryServicePath)}"

# === Request ===
response = requests.get(
    f"{BASE_URL}/external/get/directory-service/file",
    params={
        "directoryServicePath": DIRECTORY_PATH
    },
    headers={"Authorization": f"Bearer {TOKEN}"},
    stream=True
)

# === Save file ===
if response.status_code == 200:
    output_file = "${outputFile}"
    with open(output_file, 'wb') as f:
        for chunk in response.iter_content(chunk_size=8192):
            f.write(chunk)
    print(f"Downloaded: {output_file}")
else:
    print(f"Error: {response.status_code}")`;
  }

  return `import requests

# === Configuration ===
BASE_URL = ${getBaseUrl()}
TOKEN = "YOUR_BEARER_TOKEN"

# === Parameters (ZIP with folder structure) ===
DIRECTORY_PATH = "${normalizePathParam(params.directoryServicePath)}"
FILE_LIST = "${params.fileList}"  ${
    params.isPattern ? '# Wildcard pattern - returns ZIP' : ''
  }
${params.includeSub ? 'INCLUDE_SUB = True\n\n' : ''}# === Request ===
response = requests.get(
    f"{BASE_URL}/external/get/directory-service/file",
    params={
        "directoryServicePath": DIRECTORY_PATH,
        "fileList": FILE_LIST${
          params.includeSub ? ',\n        "includeSub": INCLUDE_SUB' : ''
        }
    },
    headers={"Authorization": f"Bearer {TOKEN}"},
    stream=True
)

# === Save file ===
if response.status_code == 200:
    output_file = "${outputFile}"
    with open(output_file, 'wb') as f:
        for chunk in response.iter_content(chunk_size=8192):
            f.write(chunk)
    print(f"Downloaded: {output_file}")
else:
    print(f"Error: {response.status_code}")`;
};

export const generateDirectoryGetDirectJava = (
  params: DirectoryGetDirectParams
): string => {
  const isSingle = params.singleFile;
  const outputFile = isSingle
    ? params.fileList || 'downloaded_file'
    : params.isPattern
    ? 'downloaded_files.zip'
    : 'downloaded_file';

  if (isSingle) {
    return `import java.net.URI;
import java.net.http.*;
import java.nio.file.*;
import java.net.URLEncoder;
import java.nio.charset.StandardCharsets;

public class DownloadFromDirectory {
    public static void main(String[] args) throws Exception {
        // === Configuration ===
        String BASE_URL = ${getBaseUrl()};
        String TOKEN = "YOUR_BEARER_TOKEN";

        // === Parameters (single file - no ZIP wrapper) ===
        String DIRECTORY_PATH = "${normalizePathParam(
          params.directoryServicePath
        )}";

        // === Build URL ===
        String url = String.format(
            "%s/external/get/directory-service/file?directoryServicePath=%s",
            BASE_URL,
            DIRECTORY_PATH
        );

        // === Request ===
        HttpClient client = HttpClient.newHttpClient();
        HttpRequest request = HttpRequest.newBuilder()
            .uri(URI.create(url))
            .header("Authorization", "Bearer " + TOKEN)
            .GET()
            .build();

        HttpResponse<Path> response = client.send(
            request, 
            HttpResponse.BodyHandlers.ofFile(Path.of("${outputFile}"))
        );

        System.out.println("Downloaded: ${outputFile}");
    }
}`;
  }

  return `import java.net.URI;
import java.net.http.*;
import java.nio.file.*;
import java.net.URLEncoder;
import java.nio.charset.StandardCharsets;

public class DownloadFromDirectory {
    public static void main(String[] args) throws Exception {
        // === Configuration ===
        String BASE_URL = ${getBaseUrl()};
        String TOKEN = "YOUR_BEARER_TOKEN";

        // === Parameters (ZIP with folder structure) ===
        String DIRECTORY_PATH = "${normalizePathParam(
          params.directoryServicePath
        )}";
        String FILE_LIST = "${params.fileList}";  ${
    params.isPattern ? '// Wildcard pattern - returns ZIP' : ''
  }

        // === Build URL ===
        String url = String.format(
            "%s/external/get/directory-service/file?directoryServicePath=%s&fileList=%s${
              params.includeSub ? '&includeSub=true' : ''
            }",
            BASE_URL,
            DIRECTORY_PATH,
            URLEncoder.encode(FILE_LIST, StandardCharsets.UTF_8)
        );

        // === Request ===
        HttpClient client = HttpClient.newHttpClient();
        HttpRequest request = HttpRequest.newBuilder()
            .uri(URI.create(url))
            .header("Authorization", "Bearer " + TOKEN)
            .GET()
            .build();

        HttpResponse<Path> response = client.send(
            request, 
            HttpResponse.BodyHandlers.ofFile(Path.of("${outputFile}"))
        );

        System.out.println("Downloaded: ${outputFile}");
    }
}`;
};

export const generateDirectoryGetDirectJavaScript = (
  params: DirectoryGetDirectParams
): string => {
  const isSingle = params.singleFile;
  const outputFile = isSingle
    ? params.fileList || 'downloaded_file'
    : params.isPattern
    ? 'downloaded_files.zip'
    : 'downloaded_file';

  if (isSingle) {
    return `// === Configuration ===
const BASE_URL = ${getBaseUrl()};
const TOKEN = "YOUR_BEARER_TOKEN";

// === Parameters (single file - no ZIP wrapper) ===
const DIRECTORY_PATH = "${normalizePathParam(params.directoryServicePath)}";

// === Request ===
const params = new URLSearchParams({
  directoryServicePath: DIRECTORY_PATH
});

fetch(\`\${BASE_URL}/external/get/directory-service/file?\${params}\`, {
  method: 'GET',
  headers: {
    'Authorization': \`Bearer \${TOKEN}\`
  }
})
.then(response => response.blob())
.then(blob => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = '${outputFile}';
  a.click();
})
.catch(error => console.error('Error:', error));`;
  }

  return `// === Configuration ===
const BASE_URL = ${getBaseUrl()};
const TOKEN = "YOUR_BEARER_TOKEN";

// === Parameters (ZIP with folder structure) ===
const DIRECTORY_PATH = "${normalizePathParam(params.directoryServicePath)}";
const FILE_LIST = "${params.fileList}";  ${
    params.isPattern ? '// Wildcard pattern - returns ZIP' : ''
  }

// === Request ===
const queryParams = new URLSearchParams({
  directoryServicePath: DIRECTORY_PATH,
  fileList: FILE_LIST${params.includeSub ? ',\n  includeSub: true' : ''}
});

fetch(\`\${BASE_URL}/external/get/directory-service/file?\${queryParams}\`, {
  method: 'GET',
  headers: {
    'Authorization': \`Bearer \${TOKEN}\`
  }
})
.then(response => response.blob())
.then(blob => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = '${outputFile}';
  a.click();
})
.catch(error => console.error('Error:', error));`;
};

export const generateDirectoryGetConfigCurl = (
  params: DirectoryGetConfigParams
): string => {
  const baseUrl = getBaseUrl();
  const queryParams = [
    `configName=${encodeParam(params.configName)}`,
    params.beginDate ? `beginDate=${encodeParam(params.beginDate)}` : '',
    params.endDate ? `endDate=${encodeParam(params.endDate)}` : '',
  ]
    .filter(Boolean)
    .join('&');
  return `curl -OJ "${baseUrl}/external/get/directory-service/file?${queryParams}" \\
  -H "Authorization: Bearer YOUR_BEARER_TOKEN"`;
};

export const generateDirectoryGetConfigPython = (
  params: DirectoryGetConfigParams
): string => {
  const paramsObj = [
    '"configName": CONFIG_NAME',
    params.beginDate ? '"beginDate": BEGIN_DATE' : '',
    params.endDate ? '"endDate": END_DATE' : '',
  ]
    .filter(Boolean)
    .join(', ');
  const paramsDecl = [
    `CONFIG_NAME = "${params.configName}"`,
    params.beginDate ? `BEGIN_DATE = "${params.beginDate}"` : '',
    params.endDate ? `END_DATE = "${params.endDate}"` : '',
  ]
    .filter(Boolean)
    .join('\n');
  return `import requests

# === Configuration ===
BASE_URL = ${getBaseUrl()}
TOKEN = "YOUR_BEARER_TOKEN"

# === Parameters ===
${paramsDecl}

# === Request ===
response = requests.get(
    f"{BASE_URL}/external/get/directory-service/file",
    params={${paramsObj}},
    headers={"Authorization": f"Bearer {TOKEN}"},
    stream=True
)

# === Save file ===
if response.status_code == 200:
    filename = response.headers.get('content-disposition', '').split('filename=')[-1].strip('"')
    with open(filename or "downloaded_file", 'wb') as f:
        for chunk in response.iter_content(chunk_size=8192):
            f.write(chunk)
    print(f"Downloaded: {filename}")
else:
    print(f"Error: {response.status_code}")`;
};

export const generateDirectoryGetConfigJava = (
  params: DirectoryGetConfigParams
): string => {
  const urlParts = ['"configName=" + CONFIG_NAME'];
  if (params.beginDate) urlParts.push('"&beginDate=" + BEGIN_DATE');
  if (params.endDate) urlParts.push('"&endDate=" + END_DATE');
  const urlBuild =
    'BASE_URL + "/external/get/directory-service/file?" + ' +
    urlParts.join(' + ');
  const paramDecls = [
    `String CONFIG_NAME = "${params.configName}";`,
    params.beginDate ? `String BEGIN_DATE = "${params.beginDate}";` : '',
    params.endDate ? `String END_DATE = "${params.endDate}";` : '',
  ]
    .filter(Boolean)
    .join('\n        ');
  return `import java.net.URI;
import java.net.http.*;
import java.nio.file.*;

public class DownloadByConfig {
    public static void main(String[] args) throws Exception {
        // === Configuration ===
        String BASE_URL = ${getBaseUrl()};
        String TOKEN = "YOUR_BEARER_TOKEN";

        // === Parameters ===
        ${paramDecls}

        // === Build URL ===
        String url = ${urlBuild};

        // === Request ===
        HttpClient client = HttpClient.newHttpClient();
        HttpRequest request = HttpRequest.newBuilder()
            .uri(URI.create(url))
            .header("Authorization", "Bearer " + TOKEN)
            .GET()
            .build();

        HttpResponse<Path> response = client.send(
            request, 
            HttpResponse.BodyHandlers.ofFile(Path.of("downloaded_file"))
        );

        System.out.println("Downloaded successfully");
    }
}`;
};

export const generateDirectoryGetConfigJavaScript = (
  params: DirectoryGetConfigParams
): string => {
  const queryParams = [
    `configName=\${CONFIG_NAME}`,
    params.beginDate ? `beginDate=\${BEGIN_DATE}` : '',
    params.endDate ? `endDate=\${END_DATE}` : '',
  ]
    .filter(Boolean)
    .join('&');
  const paramDecls = [
    `const CONFIG_NAME = "${params.configName}";`,
    params.beginDate ? `const BEGIN_DATE = "${params.beginDate}";` : '',
    params.endDate ? `const END_DATE = "${params.endDate}";` : '',
  ]
    .filter(Boolean)
    .join('\n');
  return `// === Configuration ===
const BASE_URL = ${getBaseUrl()};
const TOKEN = "YOUR_BEARER_TOKEN";

// === Parameters ===
${paramDecls}

// === Request ===
fetch(\`\${BASE_URL}/external/get/directory-service/file?${queryParams}\`, {
  method: 'GET',
  headers: {
    'Authorization': \`Bearer \${TOKEN}\`
  }
})
.then(response => response.blob())
.then(blob => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'downloaded_file';
  a.click();
})
.catch(error => console.error('Error:', error));`;
};

export const generateUploadCurl = (params: DirectoryUploadParams): string => {
  const baseUrl = getBaseUrl();
  const hasFileDesc =
    params.fileDescription && params.fileDescription.trim() !== '';
  const fileDescVal = hasFileDesc ? (params.fileDescription || '').trim() : '';
  const destPath = normalizePathParam(params.destinationPath);
  const lines = [
    `curl -X POST "${baseUrl}/external/put/upload" \\`,
    '  -H "Authorization: Bearer YOUR_BEARER_TOKEN" \\',
    `  -F "file=@./${encodeParam(params.fileName)}" \\`,
    `  -F "destinationPath=${destPath}"${
      hasFileDesc || params.isReplace ? ' \\' : ''
    }`,
  ];
  if (hasFileDesc) {
    lines.push(
      `  -F "fileDescription=${encodeParam(fileDescVal)}"${
        params.isReplace ? ' \\' : ''
      }`
    );
  }
  if (params.isReplace) {
    lines.push('  -F "isReplace=true"');
  }
  return lines.join('\n');
};

export const generateUploadPython = (params: DirectoryUploadParams): string => {
  const hasFileDesc =
    params.fileDescription && params.fileDescription.trim() !== '';
  const fileDescVal = hasFileDesc ? (params.fileDescription || '').trim() : '';
  const fileDescLine = hasFileDesc
    ? `        'fileDescription': "${fileDescVal.replace(/"/g, '\\"')}",\n`
    : '';
  const isReplaceLine = params.isReplace ? `        'isReplace': True,\n` : '';
  return `import requests

# === Configuration ===
BASE_URL = ${getBaseUrl()}
TOKEN = "YOUR_BEARER_TOKEN"

# === Form Fields (multipart/form-data) ===
DEST_PATH = "${normalizePathParam(params.destinationPath)}"
FILE_PATH = "./${params.fileName}"

# === Request ===
with open(FILE_PATH, 'rb') as f:
    files = {'file': (FILE_PATH.split('/')[-1], f)}
    data = {
        'destinationPath': DEST_PATH,
${fileDescLine}${isReplaceLine}    }
    
    response = requests.post(
        f"{BASE_URL}/external/put/upload",
        headers={"Authorization": f"Bearer {TOKEN}"},
        files=files,
        data=data
    )

# === Check response ===
if response.status_code == 200:
    result = response.json()
    print(f"Uploaded successfully: {result.get('path')}")
else:
    print(f"Error: {response.status_code} - {response.text}")`;
};

export const generateUploadJava = (params: DirectoryUploadParams): string => {
  const hasFileDesc =
    params.fileDescription && params.fileDescription.trim() !== '';
  const fileDescVal = hasFileDesc ? (params.fileDescription || '').trim() : '';
  const fileDescVar = hasFileDesc
    ? `String FILE_DESC = "${fileDescVal.replace(/"/g, '\\"')}";\n        `
    : '';
  const fileDescFormPart = hasFileDesc
    ? `"\\r\\n--" + boundary + "\\r\\n" +\n            "Content-Disposition: form-data; name=\\"fileDescription\\"\\r\\n\\r\\n" + FILE_DESC +\n            `
    : '';
  const isReplaceFormPart = params.isReplace
    ? `"\\r\\n--" + boundary + "\\r\\n" +\n            "Content-Disposition: form-data; name=\\"isReplace\\"\\r\\n\\r\\n" + true +\n            `
    : '';
  return `import java.net.URI;
import java.net.http.*;
import java.nio.file.*;
import java.util.*;

public class UploadFile {
    public static void main(String[] args) throws Exception {
        // === Configuration ===
        String BASE_URL = ${getBaseUrl()};
        String TOKEN = "YOUR_BEARER_TOKEN";

        // === Form Fields (multipart/form-data) ===
        String DEST_PATH = "${normalizePathParam(params.destinationPath)}";
        String FILE_PATH = "./${params.fileName}";
        ${fileDescVar}
        // === Build multipart body ===
        String boundary = UUID.randomUUID().toString();
        Path filePath = Path.of(FILE_PATH);
        
        String body = "--" + boundary + "\\r\\n" +
            "Content-Disposition: form-data; name=\\"file\\"; filename=\\"" + filePath.getFileName() + "\\"\\r\\n" +
            "Content-Type: application/octet-stream\\r\\n\\r\\n";
        
        byte[] fileBytes = Files.readAllBytes(filePath);
        
        String formData = "\\r\\n--" + boundary + "\\r\\n" +
            "Content-Disposition: form-data; name=\\"destinationPath\\"\\r\\n\\r\\n" + DEST_PATH +
            ${fileDescFormPart}${isReplaceFormPart}"\\r\\n--" + boundary + "--\\r\\n";

        // === Request ===
        HttpClient client = HttpClient.newHttpClient();
        HttpRequest request = HttpRequest.newBuilder()
            .uri(URI.create(BASE_URL + "/external/put/upload"))
            .header("Authorization", "Bearer " + TOKEN)
            .header("Content-Type", "multipart/form-data; boundary=" + boundary)
            .POST(HttpRequest.BodyPublishers.ofByteArray(
                concat(body.getBytes(), fileBytes, formData.getBytes())
            ))
            .build();

        HttpResponse<String> response = client.send(
            request, 
            HttpResponse.BodyHandlers.ofString()
        );

        System.out.println("Response: " + response.body());
    }
    
    static byte[] concat(byte[]... arrays) {
        int length = 0;
        for (byte[] arr : arrays) length += arr.length;
        byte[] result = new byte[length];
        int pos = 0;
        for (byte[] arr : arrays) {
            System.arraycopy(arr, 0, result, pos, arr.length);
            pos += arr.length;
        }
        return result;
    }
}`;
};

export const generateUploadJavaScript = (
  params: DirectoryUploadParams
): string => {
  const hasFileDesc =
    params.fileDescription && params.fileDescription.trim() !== '';
  const fileDescVal = hasFileDesc ? (params.fileDescription || '').trim() : '';
  const fileDescConst = hasFileDesc
    ? `const FILE_DESC = "${fileDescVal.replace(/"/g, '\\"')}";\n`
    : '';
  const fileDescAppend = hasFileDesc
    ? `formData.append('fileDescription', FILE_DESC);\n`
    : '';
  const isReplaceAppend = params.isReplace
    ? `formData.append('isReplace', true);\n`
    : '';
  return `// === Configuration ===
const BASE_URL = ${getBaseUrl()};
const TOKEN = "YOUR_BEARER_TOKEN";

// === Form Fields (multipart/form-data) ===
const DEST_PATH = "${normalizePathParam(params.destinationPath)}";
${fileDescConst}
// === Create FormData ===
// Assuming 'fileInput' is an <input type="file"> element
const fileInput = document.getElementById('fileInput');
const file = fileInput.files[0];

const formData = new FormData();
formData.append('file', file);
formData.append('destinationPath', DEST_PATH);
${fileDescAppend}${isReplaceAppend}

// === Request ===
fetch(\`\${BASE_URL}/external/put/upload\`, {
  method: 'POST',
  headers: {
    'Authorization': \`Bearer \${TOKEN}\`
    // Note: Don't set Content-Type - browser sets it with boundary
  },
  body: formData
})
.then(response => response.json())
.then(data => {
  console.log('Uploaded successfully:', data.path);
})
.catch(error => console.error('Error:', error));`;
};

export const generateDeleteCurl = (params: DirectoryDeleteParams): string => {
  const baseUrl = getBaseUrl();
  const serverPath = normalizePathParam(params.directoryServicePath);
  const recursiveParam = params.isRecursive ? '&isRecursive=true' : '';
  return `curl -X POST "${baseUrl}/external/del?serverPath=${serverPath}&fileList=${encodeParam(
    params.fileList
  )}${recursiveParam}" \\
  -H "Authorization: Bearer YOUR_BEARER_TOKEN"`;
};

export const generateDeletePython = (params: DirectoryDeleteParams): string => {
  return `import requests

# === Configuration ===
BASE_URL = ${getBaseUrl()}
TOKEN = "YOUR_BEARER_TOKEN"

# === Parameters ===
SERVER_PATH = "${normalizePathParam(params.directoryServicePath)}"
FILE_LIST = "${params.fileList}"  ${
    params.isPattern ? '# Wildcard pattern' : ''
  }
IS_RECURSIVE = ${params.isRecursive}

# === WARNING: This will permanently delete the file(s)! ===

# === Request ===
response = requests.post(
    f"{BASE_URL}/external/del",
    params={
        "serverPath": SERVER_PATH,
        "fileList": FILE_LIST,
        "isRecursive": IS_RECURSIVE
    },
    headers={"Authorization": f"Bearer {TOKEN}"}
)

# === Check response ===
if response.status_code == 200:
    print("File(s) deleted successfully")
else:
    print(f"Error: {response.status_code} - {response.text}")`;
};

export const generateDeleteJava = (params: DirectoryDeleteParams): string => {
  return `import java.net.URI;
import java.net.http.*;
import java.net.URLEncoder;
import java.nio.charset.StandardCharsets;

public class DeleteFile {
    public static void main(String[] args) throws Exception {
        // === Configuration ===
        String BASE_URL = ${getBaseUrl()};
        String TOKEN = "YOUR_BEARER_TOKEN";

        // === Parameters ===
        String SERVER_PATH = "${normalizePathParam(
          params.directoryServicePath
        )}";
        String FILE_LIST = "${params.fileList}";  ${
    params.isPattern ? '// Wildcard pattern' : ''
  }
        boolean IS_RECURSIVE = ${params.isRecursive};

        // === WARNING: This will permanently delete the file(s)! ===

        // === Build URL ===
        String url = String.format(
            "%s/external/del?serverPath=%s&fileList=%s&isRecursive=%s",
            BASE_URL,
            SERVER_PATH,
            URLEncoder.encode(FILE_LIST, StandardCharsets.UTF_8),
            IS_RECURSIVE
        );

        // === Request ===
        HttpClient client = HttpClient.newHttpClient();
        HttpRequest request = HttpRequest.newBuilder()
            .uri(URI.create(url))
            .header("Authorization", "Bearer " + TOKEN)
            .POST(HttpRequest.BodyPublishers.noBody())
            .build();

        HttpResponse<String> response = client.send(
            request, 
            HttpResponse.BodyHandlers.ofString()
        );

        System.out.println("Status: " + response.statusCode());
    }
}`;
};

export const generateDeleteJavaScript = (
  params: DirectoryDeleteParams
): string => {
  return `// === Configuration ===
const BASE_URL = ${getBaseUrl()};
const TOKEN = "YOUR_BEARER_TOKEN";

// === Parameters ===
const SERVER_PATH = "${normalizePathParam(params.directoryServicePath)}";
const FILE_LIST = "${params.fileList}";  ${
    params.isPattern ? '// Wildcard pattern' : ''
  }
const IS_RECURSIVE = ${params.isRecursive};

// === WARNING: This will permanently delete the file(s)! ===

// === Request ===
const queryParams = new URLSearchParams({
  serverPath: SERVER_PATH,
  fileList: FILE_LIST,
  isRecursive: IS_RECURSIVE
});

fetch(\`\${BASE_URL}/external/del?\${queryParams}\`, {
  method: 'POST',
  headers: {
    'Authorization': \`Bearer \${TOKEN}\`
  }
})
.then(response => {
  if (response.ok) {
    console.log('File(s) deleted successfully');
  } else {
    console.error('Error:', response.status);
  }
})
.catch(error => console.error('Error:', error));`;
};

export const generateCode = (
  language: CodeLanguage,
  apiType: string,
  params: CodeSnippetParams
): string => {
  const generators = {
    'download-direct': {
      curl: generateDownloadDirectCurl,
      python: generateDownloadDirectPython,
      java: generateDownloadDirectJava,
      javascript: generateDownloadDirectJavaScript,
    },
    'download-config': {
      curl: generateDownloadConfigCurl,
      python: generateDownloadConfigPython,
      java: generateDownloadConfigJava,
      javascript: generateDownloadConfigJavaScript,
    },
    'download-file-list': {
      curl: generateFileListCurl,
      python: generateFileListPython,
      java: generateFileListJava,
      javascript: generateFileListJavaScript,
    },
    'download-file-part': {
      curl: generateFilePartCurl,
      python: generateFilePartPython,
      java: generateFilePartJava,
      javascript: generateFilePartJavaScript,
    },
    'directory-get-direct': {
      curl: generateDirectoryGetDirectCurl,
      python: generateDirectoryGetDirectPython,
      java: generateDirectoryGetDirectJava,
      javascript: generateDirectoryGetDirectJavaScript,
    },
    'directory-get-config': {
      curl: generateDirectoryGetConfigCurl,
      python: generateDirectoryGetConfigPython,
      java: generateDirectoryGetConfigJava,
      javascript: generateDirectoryGetConfigJavaScript,
    },
    'directory-upload': {
      curl: generateUploadCurl,
      python: generateUploadPython,
      java: generateUploadJava,
      javascript: generateUploadJavaScript,
    },
    'directory-delete': {
      curl: generateDeleteCurl,
      python: generateDeletePython,
      java: generateDeleteJava,
      javascript: generateDeleteJavaScript,
    },
  } as Record<string, Record<CodeLanguage, (p: CodeSnippetParams) => string>>;

  const generator = generators[apiType]?.[language];
  if (!generator) {
    return `// Code generator not available for ${apiType} in ${language}`;
  }

  return generator(params);
};

/**
 * Match a filename against a pattern string.
 * Supports comma-separated patterns: "*.pdf,*.doc" matches .pdf OR .doc files.
 * Supports wildcard *: "report_*" matches any file starting with "report_".
 */
export const matchesWildcard = (filename: string, pattern: string): boolean => {
  const patterns = pattern
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean);
  if (patterns.length === 0) return false;

  return patterns.some((singlePattern) => {
    if (!singlePattern.includes('*')) {
      return filename === singlePattern;
    }

    const regexPattern = singlePattern
      .replace(/[.+?^${}()|[\]\\]/g, '\\$&')
      .replace(/\*/g, '.*');

    return new RegExp(`^${regexPattern}$`).test(filename);
  });
};
